import { useEffect, useRef, useState } from "react";
import styles from "./UserChatMessage.module.css";

interface Props {
  message: string;
  isLast?: boolean;
  onEdit?: (newText: string) => void;
  disableEdit?: boolean;
}

export const UserChatMessage = ({
  message,
  isLast,
  onEdit,
  disableEdit,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message);
  const [lockedWidth, setLockedWidth] = useState<number | undefined>(undefined);

  const textRef = useRef<HTMLDivElement | null>(null);
  const messageRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  // 1. Measure Overflow (Only if not editing)
  useEffect(() => {
    if (isEditing) return;

    const textEl = textRef.current;
    const containerEl = messageRef.current;

    if (!textEl || !containerEl) return;

    const wasExpanded = containerEl.classList.contains(styles.expanded);

    // Temporarily force collapsed to measure truncation
    containerEl.classList.remove(styles.expanded);
    containerEl.classList.add(styles.collapsed);

    requestAnimationFrame(() => {
      if (!textEl || !containerEl) return;

      const overflowing = textEl.scrollHeight > textEl.clientHeight;
      setIsTruncated(overflowing);

      // Restore previous expanded/collapsed state
      containerEl.classList.remove(styles.collapsed, styles.expanded);
      containerEl.classList.add(
        wasExpanded ? styles.expanded : styles.collapsed,
      );
    });
  }, [message, isEditing]);

  // 2. Click Outside Logic (Exit edit mode)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        messageRef.current &&
        !messageRef.current.contains(event.target as Node)
      ) {
        handleCancel();
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // 3. Actions
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
    } catch (err) {
      console.error("Failed to copy text", err);
    }
  };

  const handleStartEdit = () => {
    if (disableEdit) return;

    if (messageRef.current) {
      setLockedWidth(messageRef.current.getBoundingClientRect().width);
    }

    setEditText(message);
    setIsEditing(true);

    // Focus textarea and move cursor to end
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 0);
  };

  const handleSave = () => {
    const trimmed = editText.trim();
    if (trimmed !== "" && trimmed !== message) {
      onEdit?.(trimmed);
    }
    setIsEditing(false);
    setLockedWidth(undefined);
  };

  const handleCancel = () => {
    setEditText(message);
    setIsEditing(false);
    setLockedWidth(undefined);
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditText(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className={styles.container}>
      {/* EXTERNAL ACTION BUTTONS (Left of Bubble) */}
      {!isEditing && (
        <div className={styles.actions}>
          {/* Copy Button */}
          <button
            type="button"
            className={`${styles.iconButton} ${styles.externalButton}`}
            onClick={handleCopy}
            aria-label="Copy message"
            title="Copy"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.actionIcon}
            >
              <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
            </svg>
          </button>

          {/* Edit Button (Only last message) */}
          {isLast && onEdit && (
            <button
              type="button"
              className={`${styles.iconButton} ${styles.externalButton}`}
              onClick={handleStartEdit}
              disabled={disableEdit}
              aria-label="Edit message"
              title={disableEdit ? "Wait for response to complete" : "Edit"}
            >
              <svg viewBox="0 0 24 24" fill="none" className={styles.editIcon}>
                <path
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* MESSAGE BUBBLE */}
      <div
        ref={messageRef}
        className={`${styles.message} ${
          expanded ? styles.expanded : styles.collapsed
        }`}
        style={{
          minWidth: isEditing && lockedWidth ? `${lockedWidth}px` : undefined,
        }}
      >
        {isEditing ? (
          <div className={styles.editForm}>
            <div className={styles.inputStack}>
              {/* Hidden mirror div dictates size */}
              <div className={styles.mirrorText}>{editText + "\u200b"}</div>

              {/* Textarea sits on top */}
              <textarea
                ref={textareaRef}
                className={styles.editTextarea}
                value={editText}
                onChange={handleTextareaInput}
                onKeyDown={handleKeyDown}
                rows={1}
              />
            </div>

            <div className={styles.editActions}>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.cancelButton}`}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.saveButton}`}
                onClick={handleSave}
              >
                Send
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.textWrapper}>
              <div ref={textRef} className={styles.text}>
                {message}
              </div>
            </div>

            {/* Internal Expand Button (Only if truncated) */}
            {isTruncated && (
              <div className={styles.buttonWrapper}>
                <button
                  type="button"
                  className={`${styles.iconButton} ${styles.toggleButton}`}
                  onClick={toggleExpanded}
                  aria-label={expanded ? "Collapse message" : "Expand message"}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={`${styles.icon} ${
                      expanded ? styles.iconUp : styles.iconDown
                    }`}
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
